import { useState, useMemo, useEffect } from "react";
import { CheckCircle2, Printer, CalendarDays, Loader2, AlertTriangle, LogIn, Tag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AuthPanel } from "./AuthPanel";

const nightsBetween = (a, b) => Math.max(0, Math.round((new Date(b) - new Date(a)) / 86400000));

export const BookingModal = ({ room, defaults, open, onClose }) => {
  const [checkIn, setCheckIn] = useState(defaults.checkIn);
  const [checkOut, setCheckOut] = useState(defaults.checkOut);
  const [guests, setGuests] = useState(defaults.guests || 2);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [arrival, setArrival] = useState("");
  const [requests, setRequests] = useState("");
  const [submitting, setSubmitting] = useState("");
  const [confirmed, setConfirmed] = useState(null);
  const [bookedRanges, setBookedRanges] = useState([]);
  const [couponInput, setCouponInput] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [appliedOffer, setAppliedOffer] = useState(null);
  const [activeOffers, setActiveOffers] = useState([]);
  const [quote, setQuote] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user) { setName((n) => n || user.name || ""); setEmail((e) => e || user.email || ""); setPhone((p) => p || user.phone || ""); }
  }, [user]);

  useEffect(() => {
    if (room && open) {
      api.get(`/rooms/${room.room_id}/booked`).then(({ data }) => setBookedRanges(data)).catch(() => setBookedRanges([]));
      api.get("/offers/active").then(({ data }) => setActiveOffers(data)).catch(() => setActiveOffers([]));
    }
  }, [room, open]);

  const nights = useMemo(() => nightsBetween(checkIn, checkOut), [checkIn, checkOut]);

  useEffect(() => {
    setQuote(null);
    if (room && open && nights >= 1) {
      api.get(`/rooms/${room.room_id}/quote`, { params: { check_in: checkIn, check_out: checkOut } })
        .then(({ data }) => setQuote(data)).catch(() => setQuote(null));
    }
  }, [room, open, checkIn, checkOut, nights]);

  const hasConflict = useMemo(
    () => bookedRanges.some((r) => r.check_in < checkOut && r.check_out > checkIn),
    [bookedRanges, checkIn, checkOut]
  );
  const roomRate = quote ? quote.room_rate : (room ? room.price_per_night * nights : 0);
  const discount = appliedOffer ? Math.min(appliedOffer.discount, roomRate) : 0;
  const tax = Math.round((roomRate - discount) * 0.12);
  const total = roomRate - discount + tax;

  // Re-validate coupon whenever the room rate (nights/room) changes
  useEffect(() => { setAppliedOffer(null); }, [checkIn, checkOut, room]);

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    if (nights < 1) { toast.error("Pick your dates before applying a code"); return; }
    setApplyingCoupon(true);
    try {
      const { data } = await api.get(`/offers/check/${code}`, { params: { amount: roomRate } });
      setAppliedOffer({ code: data.code, description: data.description, discount: data.discount });
      toast.success(`Offer ${data.code} applied — you save ₹${data.discount}`);
    } catch (err) {
      setAppliedOffer(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid offer code");
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => { setAppliedOffer(null); setCouponInput(""); };

  const reset = () => {
    setConfirmed(null); setName(""); setEmail(""); setPhone(""); setArrival(""); setRequests("");
    setCouponInput(""); setAppliedOffer(null);
  };

  const handleClose = () => { onClose(); setTimeout(reset, 300); };

  const bookingPayload = () => ({
    room_id: room.room_id, guest_name: name, guest_email: email, guest_phone: phone,
    check_in: checkIn, check_out: checkOut, guests, arrival_time: arrival, special_requests: requests,
    coupon_code: appliedOffer ? appliedOffer.code : "",
  });

  const preflight = () => {
    if (nights < 1) { toast.error("Check-out must be after check-in"); return false; }
    if (hasConflict) { toast.error("This room is already booked for the selected dates"); return false; }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!preflight()) return;
    setSubmitting("hotel");
    try {
      const { data } = await api.post("/bookings", bookingPayload());
      setConfirmed(data);
      toast.success("Booking confirmed!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Booking failed");
    } finally {
      setSubmitting("");
    }
  };

  if (!room) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
        {confirmed ? (
          <div data-testid="booking-confirmation-screen" className="text-center py-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center">
              <CheckCircle2 className="h-9 w-9 text-secondary" />
            </div>
            <h3 className="mt-4 font-serif text-2xl font-semibold">Booking Confirmed!</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              A confirmation {confirmed.email_sent ? "has been emailed to" : "was prepared for"} {confirmed.guest_email}.
            </p>
            <div className="mt-5 rounded-xl border border-border bg-muted/40 p-5 text-left space-y-2 text-sm">
              <Row label="Reference" value={<span className="font-mono font-bold text-primary">{confirmed.booking_ref}</span>} />
              <Row label="Room" value={confirmed.room_name} />
              <Row label="Check-in" value={confirmed.check_in} />
              <Row label="Check-out" value={confirmed.check_out} />
              <Row label="Nights" value={confirmed.nights} />
              <Row label="Guests" value={confirmed.guests} />
              <div className="border-t border-border pt-2 mt-2">
                <Row label="Room rate" value={`₹${confirmed.room_rate}`} />
                {confirmed.discount > 0 && (
                  <Row label={<span className="text-secondary">Offer {confirmed.coupon_code}</span>}
                    value={<span className="text-secondary">−₹{confirmed.discount}</span>} />
                )}
                <Row label="Taxes (12%)" value={`₹${confirmed.tax}`} />
                <Row label={<span className="font-semibold text-foreground">Total (pay at hotel)</span>}
                  value={<span className="font-mono font-bold text-secondary text-base">₹{confirmed.total}</span>} />
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              <Button variant="outline" className="flex-1 gap-2" onClick={() => window.print()} data-testid="print-receipt-button">
                <Printer className="h-4 w-4" /> Print
              </Button>
              <Button className="flex-1 bg-primary hover:bg-primary/90" onClick={handleClose} data-testid="confirmation-done-button">Done</Button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Book {room.name}</DialogTitle>
              <DialogDescription>{user ? "Confirm your dates and details. No advance payment — pay at the hotel." : "Sign in or create an account to confirm your booking."}</DialogDescription>
            </DialogHeader>
            {!user ? (
              <div className="py-4 space-y-4" data-testid="booking-signin-required">
                <div className="text-center">
                  <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center"><LogIn className="h-7 w-7" /></div>
                  <h4 className="font-semibold mt-3">Sign in to book this room</h4>
                  <p className="text-sm text-muted-foreground mt-1">Sign in or create an account to confirm your booking. Your details will be filled in automatically.</p>
                </div>
                <AuthPanel returnPath="/" testPrefix="booking" />
              </div>
            ) : (
            <form onSubmit={submit} className="space-y-4">
              {hasConflict && (
                <div data-testid="booking-conflict-warning" className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>These dates are already booked for this room. Please pick different dates.</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Check-in</Label>
                  <div className="relative">
                    <CalendarDays className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input type="date" value={checkIn} min={new Date().toISOString().split("T")[0]}
                      onChange={(e) => setCheckIn(e.target.value)} className="pl-8" data-testid="booking-checkin-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Check-out</Label>
                  <div className="relative">
                    <CalendarDays className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input type="date" value={checkOut} min={checkIn}
                      onChange={(e) => setCheckOut(e.target.value)} className="pl-8" data-testid="booking-checkout-input" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Guests</Label>
                  <select value={guests} onChange={(e) => setGuests(Number(e.target.value))} data-testid="booking-guests-select"
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                    {[1, 2, 3, 4].map((g) => <option key={g} value={g}>{g} Guest{g > 1 ? "s" : ""}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Arrival time (optional)</Label>
                  <Input type="time" value={arrival} onChange={(e) => setArrival(e.target.value)} data-testid="booking-arrival-input" />
                </div>
              </div>

              <div>
                <Label className="text-xs">Full Name</Label>
                <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Rahul Verma" data-testid="booking-modal-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="booking-modal-email-input" />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 ..." data-testid="booking-modal-phone-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Special requests (optional)</Label>
                <Textarea value={requests} onChange={(e) => setRequests(e.target.value)} rows={2} placeholder="Early check-in, extra pillows..." data-testid="booking-requests-input" />
              </div>

              <div className="rounded-xl bg-muted/50 p-4 text-sm space-y-1.5">
                <Row label={`Room · ${nights} night${nights !== 1 ? "s" : ""}${quote?.has_seasonal ? " · seasonal" : ""}`} value={`₹${roomRate}`} />
                {appliedOffer && (
                  <Row label={<span className="text-secondary font-medium">Offer {appliedOffer.code}</span>}
                    value={<span className="text-secondary font-medium">−₹{discount}</span>} />
                )}
                <Row label="Taxes & fees (12%)" value={`₹${tax}`} />
                <div className="border-t border-border pt-1.5">
                  <Row label={<span className="font-semibold text-foreground">Total</span>}
                    value={<span className="font-mono font-bold text-secondary text-base">₹{total}</span>} />
                </div>
              </div>

              <div data-testid="booking-coupon-section">
                {appliedOffer ? (
                  <div className="flex items-center justify-between rounded-lg border border-secondary/30 bg-secondary/10 p-3 text-sm" data-testid="booking-coupon-applied">
                    <span className="flex items-center gap-1.5 text-secondary font-medium"><Tag className="h-4 w-4" /> {appliedOffer.code} applied</span>
                    <button type="button" onClick={removeCoupon} className="text-xs text-muted-foreground hover:text-destructive" data-testid="booking-coupon-remove">Remove</button>
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs">Have an offer code?</Label>
                    <div className="flex gap-2">
                      <Input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder="e.g. DIWALI20"
                        className="uppercase" data-testid="booking-coupon-input" />
                      <Button type="button" variant="outline" onClick={applyCoupon} disabled={applyingCoupon || !couponInput.trim()} data-testid="booking-coupon-apply">
                        {applyingCoupon ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                      </Button>
                    </div>
                    {activeOffers.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="booking-active-offers">
                        {activeOffers.map((o) => (
                          <button key={o.code} type="button" onClick={() => setCouponInput(o.code)}
                            className="text-[11px] rounded-full border border-secondary/30 bg-secondary/10 text-secondary px-2 py-0.5 hover:bg-secondary/20 transition-colors"
                            data-testid={`booking-offer-chip-${o.code}`}>
                            {o.code} · {o.discount_type === "percent" ? `${o.value}% off` : `₹${o.value} off`}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Button type="submit" disabled={submitting !== "" || hasConflict || nights < 1}
                className="w-full h-11 bg-primary hover:bg-primary/90 gap-2" data-testid="booking-modal-submit-button">
                {submitting === "hotel" && <Loader2 className="h-4 w-4 animate-spin" />}
                {hasConflict ? "Dates Unavailable" : `Confirm Booking · ₹${total}`}
              </Button>
              <p className="text-[11px] text-center text-muted-foreground">No advance payment required. Pay directly at the hotel.</p>
            </form>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between items-center">
    <span className="text-muted-foreground">{label}</span>
    <span>{value}</span>
  </div>
);
